import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchDashboardData } from "../../RTK/dashboard/Dashboard";
import DashboardFilters from "./DashboardFilters";
import {
  Users,
  ShoppingBag,
  DollarSign,
  TrendingUp,
  ChevronRight,
  Activity,
  Calendar,
  Eye,
  ArrowUpRight,
  ArrowDownRight,
  Crown,
  Star,
  Zap,
  Target,
  BarChart3,
  PieChart,
} from "lucide-react";
import { Link } from "react-router-dom";

const Dashboard = () => {
  const dispatch = useDispatch();
  const { stats, loading, filters } = useSelector((state) => state.dashboard);

  useEffect(() => {
    dispatch(fetchDashboardData(filters));
  }, [dispatch, filters]);

  const StatCard = ({ title, value, icon: Icon, change, gradient, accentColor }) => (
    <div className={`relative bg-gradient-to-br ${gradient} p-4 sm:p-6 rounded-2xl border border-gray-700/50 hover:border-gray-600/80 transition-all duration-300 group overflow-hidden shadow-xl hover:shadow-2xl`}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-20 sm:w-32 h-20 sm:h-32 bg-white/20 rounded-full -mr-10 sm:-mr-16 -mt-10 sm:-mt-16"></div>
        <div className="absolute bottom-0 left-0 w-12 sm:w-20 h-12 sm:h-20 bg-white/10 rounded-full -ml-6 sm:-ml-10 -mb-6 sm:-mb-10"></div>
      </div>
      
      <div className="relative z-10">
        <div className="flex justify-between items-start">
          <div className={`bg-white/10 backdrop-blur-sm p-3 sm:p-4 rounded-xl border border-white/20 group-hover:scale-110 transition-transform duration-300`}>
            <Icon className={`h-5 w-5 sm:h-7 sm:w-7 ${accentColor}`} />
          </div>
          <div className="text-right">
            <span className={`inline-flex items-center gap-1 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
              change >= 0 
                ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" 
                : "bg-red-500/20 text-red-400 border border-red-500/30"
            }`}>
              {change >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {Math.abs(change)}%
            </span>
          </div>
        </div>
        
        <div className="mt-4 sm:mt-6">
          <h3 className="text-xl sm:text-3xl font-bold text-white mb-1 sm:mb-2 group-hover:scale-105 transition-transform duration-300 break-all">
            {value}
          </h3>
          <p className="text-gray-200/80 font-medium text-sm sm:text-base">{title}</p>
          <div className={`w-full h-1 ${accentColor.replace('text-', 'bg-')} rounded-full mt-2 sm:mt-3 opacity-60`}></div>
        </div>
      </div>
    </div>
  );

  if (loading) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4">
      <div className="flex flex-col items-center justify-center gap-4">
        <div className="w-16 h-16 border-4 border-yellow-500/30 border-t-yellow-500 rounded-full animate-spin"></div>
        <p className="text-white text-lg text-center">جاري تحميل البيانات...</p>
      </div>
    </div>
  );
}

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" dir="rtl">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-64 sm:w-96 h-64 sm:h-96 bg-yellow-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 w-64 sm:w-96 h-64 sm:h-96 bg-purple-500/5 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-6 sm:mb-8">
          <div className="mb-4 sm:mb-6 lg:mb-0">
            <div className="flex items-center gap-2 sm:gap-3 mb-2">
              <div className="p-2 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl">
                <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                لوحة التحكم
              </h1>
            </div>
            <p className="text-gray-400 text-sm sm:text-base lg:text-lg flex items-center gap-2">
              <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
              مراقبة أداء متجرك والتحليلات في الوقت الفعلي
            </p>
          </div>
          <DashboardFilters />
        </div>

        {/* Enhanced Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          <StatCard
            title="إجمالي المستخدمين"
            value={stats.totalUsers?.toLocaleString() || '0'}
            icon={Users}
            change={2.5}
            gradient="from-blue-600/20 to-blue-800/20"
            accentColor="text-blue-400"
          />
          <StatCard
            title="الطلبات المكتملة"
            value={`$${stats.completedOrders?.toLocaleString() || '0'}`}
            icon={ShoppingBag}
            change={15.5}
            gradient="from-emerald-600/20 to-emerald-800/20"
            accentColor="text-emerald-400"
          />
          <StatCard
            title="رصيد الموقع"
            value={`$${Number(stats.totalSiteBalance || 0).toLocaleString()}`}
            icon={DollarSign}
            change={7.2}
            gradient="from-yellow-600/20 to-yellow-800/20"
            accentColor="text-yellow-400"
          />
          <StatCard
            title="إجمالي الأرباح"
            value={`$${Number(stats.totalProfit || 0).toLocaleString()}`}
            icon={TrendingUp}
            change={11.5}
            gradient="from-purple-600/20 to-purple-800/20"
            accentColor="text-purple-400"
          />
        </div>


        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 sm:gap-6">
          {/* Orders Table - Enhanced Mobile Responsive */}
          <div className="xl:col-span-8 bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl h-[400px] sm:h-[500px] flex flex-col">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-4 sm:p-6 pb-3 sm:pb-4 flex-shrink-0 gap-3 sm:gap-0">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl">
                  <ShoppingBag className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">أحدث الطلبات</h2>
              </div>
              <Link 
                to="/order" 
                className="group flex items-center gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-xl text-yellow-400 hover:from-yellow-500/30 hover:to-orange-500/30 transition-all duration-300 text-sm sm:text-base w-full sm:w-auto justify-center sm:justify-start"
              >
                <span>عرض الكل</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            
            {/* Scrollable Content */}
            <div className="flex-1 overflow-hidden px-3 sm:px-6 pb-4 sm:pb-6">
              <div className="h-full overflow-y-auto custom-scrollbar">
                {/* Desktop Table View */}
                <div className="hidden sm:block">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-gray-800/90 backdrop-blur-sm z-10">
                      <tr className="text-right text-gray-400 border-b border-gray-700/50">
                        <th className="pb-4 pt-2 font-semibold">رقم الطلب</th>
                        <th className="pb-4 pt-2 font-semibold">المنتج</th>
                        <th className="pb-4 pt-2 font-semibold">المبلغ</th>
                        <th className="pb-4 pt-2 font-semibold">الحالة</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.filteredOrders?.map((order) => (
                        <tr key={order.id} className="border-b border-gray-700/30 hover:bg-gray-700/20 transition-colors">
                          <td className="py-4">
                            <span className="font-mono text-sm text-gray-300 bg-gray-700/50 px-2 py-1 rounded-lg">
                              {order.transaction_id}
                            </span>
                          </td>
                          <td className="py-4">
                            <div className="flex items-center gap-3">
                              <div className="relative">
                                <img
                                  src={order.image_url}
                                  alt=""
                                  className="h-10 w-10 rounded-xl object-cover border-2 border-gray-600"
                                />
                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-gray-800"></div>
                              </div>
                              <span className="text-white font-medium">{order.product}</span>
                            </div>
                          </td>
                          <td className="py-4">
                            <span className="text-emerald-400 font-bold">${order.total_price.toFixed(3)}</span>
                          </td>
                          <td className="py-4">
                            <span
                              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                                order.status === "completed"
                                  ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                                  : order.status === "cancelled"
                                  ? "bg-red-500/20 text-red-400 border-red-500/30"
                                  : "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
                              }`}
                            >
                              {order.status === "completed"
                                ? "مكتمل"
                                : order.status === "cancelled"
                                ? "ملغي"
                                : "قيد الانتظار"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="block sm:hidden space-y-3">
                  {stats.filteredOrders?.map((order) => (
                    <div key={order.id} className="bg-gradient-to-r from-gray-700/50 to-gray-800/50 p-4 rounded-xl border border-gray-600/30 hover:border-gray-500/50 transition-all duration-300">
                      {/* Order Header */}
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-mono text-xs text-gray-300 bg-gray-700/50 px-2 py-1 rounded-lg">
                          {order.transaction_id}
                        </span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium border ${
                            order.status === "completed"
                              ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                              : order.status === "cancelled"
                              ? "bg-red-500/20 text-red-400 border-red-500/30"
                              : "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
                          }`}
                        >
                          {order.status === "completed"
                            ? "مكتمل"
                            : order.status === "cancelled"
                            ? "ملغي"
                            : "قيد الانتظار"}
                        </span>
                      </div>

                      {/* Product Info */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className="relative flex-shrink-0">
                          <img
                            src={order.image_url}
                            alt=""
                            className="h-10 w-10 rounded-xl object-cover border-2 border-gray-600"
                          />
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-gray-800"></div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium text-sm truncate">{order.product}</p>
                          <p className="text-emerald-400 font-bold text-lg">${order.total_price.toFixed(3)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Popular Products - Enhanced with better mobile responsive */}
          <div className="xl:col-span-4 bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl h-[400px] sm:h-[500px] flex flex-col">
            <div className="flex items-center gap-2 sm:gap-3 p-4 sm:p-6 pb-3 sm:pb-4 flex-shrink-0">
              <div className="p-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl">
                <Star className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-white">المنتجات الشائعة</h2>
            </div>
            
            {/* Scrollable Content */}
            <div className="flex-1 overflow-hidden px-4 sm:px-6 pb-4 sm:pb-6">
              <div className="h-full overflow-y-auto custom-scrollbar space-y-3 sm:space-y-4">
                {stats.popularProducts?.map((product, index) => (
                  <div key={index} className="group flex items-center justify-between p-3 rounded-xl hover:bg-gray-700/30 transition-all duration-300 cursor-pointer">
                    <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                      <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm flex-shrink-0 ${
                        index === 0 ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white' :
                        index === 1 ? 'bg-gradient-to-r from-gray-400 to-gray-500 text-white' :
                        index === 2 ? 'bg-gradient-to-r from-amber-600 to-yellow-600 text-white' :
                        'bg-gray-600 text-gray-300'
                      }`}>
                        #{index + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-white group-hover:text-yellow-400 transition-colors text-sm sm:text-base truncate">
                          {product.name}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-400">
                          {product.order_count} 
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 group-hover:text-yellow-400 group-hover:translate-x-1 transition-all flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Agents Section - Enhanced mobile responsive */}
          <div className="xl:col-span-8 bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl h-[400px] sm:h-[500px] flex flex-col">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 sm:p-6 pb-3 sm:pb-4 flex-shrink-0 gap-3 sm:gap-0">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-2 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl">
                  <Target className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">الوكلاء</h2>
              </div>
              <Link 
                to="/agent" 
                className="group flex items-center gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/30 rounded-xl text-blue-400 hover:from-blue-500/30 hover:to-cyan-500/30 transition-all duration-300 text-sm sm:text-base w-full sm:w-auto justify-center sm:justify-start"
              >
                <span>عرض الكل</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-hidden px-3 sm:px-6 pb-4 sm:pb-6">
              <div className="h-full overflow-y-auto custom-scrollbar">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                  {stats.agentBalances?.map((agent, index) => (
                    <div
                      key={index}
                      className="group bg-gradient-to-r from-gray-700/50 to-gray-800/50 p-3 sm:p-4 rounded-xl flex items-center gap-3 sm:gap-4 border border-gray-600/30 hover:border-gray-500/50 hover:from-gray-600/50 hover:to-gray-700/50 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative flex-shrink-0">
                        <img
                          src={agent.icon}
                          alt={agent.agent_name}
                          className="h-10 w-10 sm:h-12 sm:w-12 rounded-full border-2 border-gray-500 group-hover:border-yellow-400 transition-colors"
                        />
                        <div className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-green-500 rounded-full border-2 border-gray-800 flex items-center justify-center">
                          <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-white rounded-full animate-pulse"></div>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium group-hover:text-yellow-400 transition-colors text-sm sm:text-base truncate">
                          {agent.agent_name}
                        </h3>
                        <p className="text-yellow-400 font-bold text-base sm:text-lg break-all">
                          ${Number(agent.balance).toLocaleString()}
                        </p>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Top Users - Enhanced mobile responsive */}
          <div className="xl:col-span-4 bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl h-[400px] sm:h-[500px] flex flex-col">
            <div className="flex items-center gap-2 sm:gap-3 p-4 sm:p-6 pb-3 sm:pb-4 flex-shrink-0">
              <div className="p-2 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
                <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-white">أفضل المستخدمين</h2>
            </div>
            
            {/* Scrollable Content */}
            <div className="flex-1 overflow-hidden px-4 sm:px-6 pb-4 sm:pb-6">
              <div className="h-full overflow-y-auto custom-scrollbar space-y-3 sm:space-y-4">
                {stats.topUsers?.map((user, index) => (
                  <div key={index} className="group flex items-center justify-between p-3 rounded-xl hover:bg-gray-700/30 transition-all duration-300 cursor-pointer">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                      <div className="relative flex-shrink-0">
                        <div
                          className="h-8 w-8 sm:h-10 sm:w-10 rounded-full border-2 border-gray-500 group-hover:border-yellow-400 transition-colors overflow-hidden"
                          dangerouslySetInnerHTML={{ __html: user.svg_image }}
                        />
                        {index < 3 && (
                          <div className={`absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-gray-800 flex items-center justify-center text-xs font-bold ${
                            index === 0 ? 'bg-yellow-400 text-black' :
                            index === 1 ? 'bg-gray-300 text-black' :
                            'bg-amber-600 text-white'
                          }`}>
                            {index + 1}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-white font-medium group-hover:text-yellow-400 transition-colors text-sm sm:text-base truncate">
                          {user.username}
                        </p>
                        <p className="text-xs sm:text-sm text-emerald-400 font-semibold break-all">
                          ${user.order_count}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                      {index < 3 && <Zap className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400" />}
                      <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 group-hover:text-yellow-400 group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        @media (min-width: 640px) {
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(55, 65, 81, 0.3);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #eab308, #f59e0b);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, #fbbf24, #f59e0b);
        }
      `}</style>
    </div>
  );
};

export default Dashboard;